<script setup lang="ts">
interface Star {
  x: number
  y: number
  size: number
}

const props = withDefaults(
  defineProps<{
    starCount?: number
    color?: string
    speed?:
      | 'slowest'
      | 'slower'
      | 'slow'
      | 'normal'
      | 'fast'
      | 'faster'
      | 'fastest'
    size?: { min: number; max: number }
  }>(),
  {
    starCount: 200,
    color: 'var(--ui-primary)',
    speed: 'normal',
    size: () => ({
      min: 25,
      max: 100
    })
  }
)

// Duration bounds used to map speed -> size (smaller duration => smaller stars)
const MIN_DURATION = 100
const MAX_DURATION = 700

// Generate random star positions and sizes
const generateStars = (count: number, duration: number): Star[] => {
  return Array.from({ length: count }, () => {
    const x = Math.floor(Math.random() * 2000)
    const y = Math.floor(Math.random() * 2000)

    // Determine size: if props.size is a number use it, otherwise make size
    // depend on duration so faster layers (smaller duration) produce smaller stars.
    let size: number
    if (typeof props.size === 'number') {
      size = props.size
    } else {
      const min = props.size.min
      const max = props.size.max

      // clamp duration and compute a normalized factor [0..1]
      const clamped = Math.max(MIN_DURATION, Math.min(MAX_DURATION, duration))
      const t = (clamped - MIN_DURATION) / Math.max(1, MAX_DURATION - MIN_DURATION)

      // maximum size allowed for this duration (faster = smaller max)
      const maxForDuration = min + (max - min) * t

      // random size between min and maxForDuration
      size = Math.random() * (maxForDuration - min) + min
    }

    return { x, y, size }
  })
}

// Define speed configurations once
const speedMap = {
  slowest: { duration: 700, opacity: 0.05, ratio: 0.1429 },
  slower: { duration: 600, opacity: 0.1, ratio: 0.1429 },
  slow: { duration: 500, opacity: 0.15, ratio: 0.1429 },
  normal: { duration: 400, opacity: 0.2, ratio: 0.1429 },
  fast: { duration: 300, opacity: 0.25, ratio: 0.1429 },
  faster: { duration: 200, opacity: 0.3, ratio: 0.1429 },
  fastest: { duration: 100, opacity: 0.35, ratio: 0.1429 }
}

// Use a more efficient approach to generate and store stars
const stars = useState<{
  slowest: Star[]
  slower: Star[]
  slow: Star[]
  normal: Star[]
  fast: Star[]
  faster: Star[]
  fastest: Star[]
}>('stars', () => {
  return {
    slowest: generateStars(
      Math.floor(props.starCount * speedMap.slowest.ratio),
      speedMap.slowest.duration
    ),
    slower: generateStars(
      Math.floor(props.starCount * speedMap.slower.ratio),
      speedMap.slower.duration
    ),
    slow: generateStars(
      Math.floor(props.starCount * speedMap.slow.ratio),
      speedMap.slow.duration
    ),
    normal: generateStars(
      Math.floor(props.starCount * speedMap.normal.ratio),
      speedMap.normal.duration
    ),
    fast: generateStars(
      Math.floor(props.starCount * speedMap.fast.ratio),
      speedMap.fast.duration
    ),
    faster: generateStars(
      Math.floor(props.starCount * speedMap.faster.ratio),
      speedMap.faster.duration
    ),
    fastest: generateStars(
      Math.floor(props.starCount * speedMap.fastest.ratio),
      speedMap.fastest.duration
    )
  }
})

// Compute star layers with different speeds and opacities
const starLayers = computed(() => [
  { stars: stars.value.slowest, ...speedMap.slowest },
  { stars: stars.value.slower, ...speedMap.slower },
  { stars: stars.value.slow, ...speedMap.slow },
  { stars: stars.value.normal, ...speedMap.normal },
  { stars: stars.value.fast, ...speedMap.fast },
  { stars: stars.value.faster, ...speedMap.faster },
  { stars: stars.value.fastest, ...speedMap.fastest }
])
</script>

<template>
  <div
    class="pointer-events-none absolute inset-0 top-0 z-[-1] overflow-hidden"
  >
    <svg
      class="pointer-events-none absolute inset-0"
      viewBox="0 0 1017 181"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g opacity="0.5">
        <mask
          id="path-1-inside-1_846_160841"
          fill="white"
        >
          <path d="M0 0H1017V181H0V0Z" />
        </mask>
        <path
          d="M0 0H1017V181H0V0Z"
          fill="url(#paint0_radial_846_160841)"
          fill-opacity="0.22"
        />
      </g>
      <line
        x1="0"
        y1="1.5"
        x2="1017"
        y2="1.5"
        stroke="url(#paint1_linear_846_160841)"
        stroke-width="2"
        stroke-linecap="butt"
        vector-effect="non-scaling-stroke"
      />
      <defs>
        <radialGradient
          id="paint0_radial_846_160841"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(508.999 19.5) rotate(90.177) scale(161.501 509.002)"
        >
          <stop stop-color="var(--ui-primary)" />
          <stop
            offset="1"
            stop-color="var(--ui-primary)"
            stop-opacity="0"
          />
        </radialGradient>
        <linearGradient
          id="paint1_linear_846_160841"
          x1="10.9784"
          y1="91"
          x2="1017"
          y2="90.502"
          gradientUnits="userSpaceOnUse"
        >
          <stop
            stop-color="var(--ui-primary)"
            stop-opacity="0"
          />
          <stop
            offset="0.395"
            stop-color="var(--ui-primary)"
          />
          <stop
            offset="1"
            stop-color="var(--ui-primary)"
            stop-opacity="0"
          />
        </linearGradient>
      </defs>
    </svg>

    <div class="stars absolute inset-x-0 top-0 size-full">
      <div
        v-for="(layer, index) in starLayers"
        :key="index"
        class="star-layer"
        :style="{
          '--star-duration': `${layer.duration}s`,
          '--star-opacity': layer.opacity,
          '--star-color': color
        }"
      >
        <WoodRings
          v-for="(star, starIndex) in layer.stars"
          :key="starIndex"
          class="star absolute rounded-full"
          :style="{
            left: `${star.x}px`,
            top: `${star.y}px`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            color: 'var(--star-color)',
            opacity: 'var(--star-opacity)'
          }"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.stars {
  left: 50%;
  transform: translate(-50%);
  -webkit-mask-image: linear-gradient(
    180deg,
    rgba(217, 217, 217, 0) 0%,
    rgba(217, 217, 217, 0.8) 25%,
    #d9d9d9 50%,
    rgba(217, 217, 217, 0.8) 75%,
    rgba(217, 217, 217, 0) 100%
  );
  mask-image: linear-gradient(
    180deg,
    rgba(217, 217, 217, 0) 0%,
    rgba(217, 217, 217, 0.8) 25%,
    #d9d9d9 50%,
    rgba(217, 217, 217, 0.8) 75%,
    rgba(217, 217, 217, 0) 100%
  );
  -webkit-mask-size: cover;
  mask-size: cover;
}

.star-layer {
  animation: risingStarsAnimation linear infinite;
  animation-duration: var(--star-duration);
  will-change: transform;
}

@keyframes risingStarsAnimation {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-2000px);
  }
}
</style>
